import "./history-view-report-page.less"
import Layout from "../layouts/layout"
import {
  DrawingSeries,
  MapAnswer,
  MapPin,
  PlayHistoryReport, PlayPollAnswer,
  PlayQuizAnswer,
} from "../play-model"
import HistoryReportSummary from "./history-report-summary"
import { ConnectScreenView } from "../screens/connect"
import { QuizScreenView } from "../screens/quiz"
import {atOrNull, findWithIndex, format, hasText} from "utils/helpers"
import { PresentationScreenView } from "../screens/presentation"
import { DragDropScreenView } from "../screens/drag-drop"
import {DEFAULT_DRAW_RADIUS, DrawScreenView} from "../screens/draw"
import { KeyboardScreenView } from "../screens/keyboard"
import { MapScreenView } from "../screens/map"
import {
  computeInitialDragDropStateFromScreen,
  convertConnectPairToPlayPair,
} from "play/reducers"
import { useEffect } from "react"
import { useParams } from "react-router-dom"
import { usePlayState } from "play/hooks/use-play-state"
import { serverGetReportForCurrentStudent } from "utils/server"
import { StudentInfo } from "models/reports-model"
import { computeStatsForStudent } from "models/reports-compute-stats"
import {
  ConnectScreen, DragDropAnswer,
  DragDropScreen,
  DrawScreen,
  KeyboardScreen,
  MapScreen, OpenTextScreen, PollScreen,
  PresentationScreen,
  QuizScreen,
  ReportedConnectAnswer, ReportedDragDropAnswer, ReportedDrawingAnswer, ReportedDrawingCommand,
  ReportedKeyboardAnswer,
  ReportedMapAnswer, ReportedOpenTextAnswer, ReportedPollAnswer,
  ReportedQuizAnswer,
  ScreenType,
} from "models/tables"
import {useMessages} from "play/language/language";
import {PollScreenView} from "play/screens/poll";
import OpenTextView from "admin/pages/view/open-text-view";
import {OpenTextScreenView} from "play/screens/open-text";

export default function HistoryViewReportPage() {
  const { reportId } = useParams()
  return <HistoryViewReportView reportId={reportId!} />
}

export function HistoryViewReportView(props: { reportId: string }) {
  const { history, modifyState } = usePlayState()
  const stats = history.stats
  useEffect(() => {
    const exists = stats.findIndex((x) => x.report.id === props.reportId) !== -1

    if (exists) return

    if (history.loadingReportId === props.reportId) return

    modifyState((state) => {
      state.internalPage = "Loading"
      state.loadingReason = "Loading"
      state.serverError = null
      state.history.loadingReportId = props.reportId
    })

    serverGetReportForCurrentStudent({ reportId: props.reportId })
      .then((report) => {
        modifyState((state) => {
          state.internalPage = null
          state.loadingReason = null
          state.history.loadingReportId = null
          
          if (report) {
            if (state.fullName && !!state.loggedUser && state.loggedUser.id) {
              const studentInfo: StudentInfo = {
                userId: state.loggedUser.id,
                name: state.fullName,
              }
              const stats = computeStatsForStudent(report, studentInfo)
              state.history.stats = [{ report, stats }]
            } else {
              state.serverError = "NotLoggedIn"
            }
          } else {
            state.serverError = "ReportNotFound"
          }
        })
      })
      .catch((e) => {
        modifyState((state) => {
          state.internalPage = null
          state.loadingReason = null
          state.serverError = "Generic"
          state.serverErrorData = e
          state.history.loadingReportId = null
        })
      })
  }, [props.reportId])
  const report = stats.find((x) => x.stats.reportId === props.reportId)

  return (
    <Layout
      timerEnabled={false}
      screenCounterEnabled={false}
      nextEnabled={false}
      prevEnabled={false}
      pointsEnabled={false}
      scrollingEnabled={true}
    >
      <div className="history-view-report-page">
        {report && (
          <>
            <div className="header-wrapper">
              <HistoryReportSummary report={report} expandable={false} />
            </div>
            <div className="body-wrapper">
              <ReportStats report={report} />
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}

function ReportStats(props: { report: PlayHistoryReport }) {
  const msg = useMessages()
  const { stats } = props.report

  function ignore() {}

  return (
    <>
      {stats.screens
        .filter((x) => !!x.screen)
        .map((screenStats) => {
          const screen = screenStats.screen!

          switch (screen.type) {
            case ScreenType.Presentation: {
              const presentation = screen as PresentationScreen

              return (
                <PresentationScreenView
                  key={presentation.id}
                  screenIndex={presentation.ordinal}
                  questionVideo={presentation.youtubeUrl}
                  questionVideoStart={presentation.youtubeStart || 0}
                  questionVideoEnd={presentation.youtubeEnd || 0}
                  questionText={presentation.question}
                  questionAudio={presentation.questionAudioUrl || ""}
                  questionImage={presentation.questionImageUrl || ""}
                  questionImageWidth={presentation.questionImageWidth}
                  questionImageHeight={presentation.questionImageHeight}
                />
              )
            }
            case ScreenType.DragDrop: {
              const dragDropScreen = screen as DragDropScreen
              const dragDrop = computeInitialDragDropStateFromScreen({
                dragDropCategories: dragDropScreen.categories,
                shuffleDragDropZones: false,
                shuffleDragDropAnswerPiles: false,
              })
              
              for (let reportedAnswer of (screenStats.answers as unknown as ReportedDragDropAnswer[])) {
                for (let move of reportedAnswer.moves) {
                  const category = atOrNull(dragDropScreen.categories, move.toCategoryIndex)
                  if (!category) continue
                  
                  const originalAnswer = category.answers.find(x => x.id === move.originalAnswerId)
                  if (!originalAnswer) continue
                  
                  const zonesForAnswer = dragDrop.dropZones.filter(x => x.categoryId === category.id)
                  const sortedZones = zonesForAnswer.map(zone => {
                    let sum = 0
                    for (let target of zone.targets) {
                      sum += target.answers.length
                    }
                    return {
                      sum,
                      zone
                    }
                  }).sort((a, b) => a.sum - b.sum)

                  if (sortedZones.length === 0) continue

                  const zone = sortedZones[0].zone
                  const targets = zone.targets
                    .map((x, i) => ({ t: x, idx: i }))
                    .sort((a, b) => (a.t.answers.length - b.t.answers.length) * 1000 + (a.idx - b.idx))
                  if (targets.length === 0) continue

                  const target = targets[0].t
                  target.answers.push({
                    id: move.originalAnswerId,
                    categoryId: category.id,
                    dropTargetIndex: -1,
                    text: originalAnswer.text || "",
                    imageUrl: originalAnswer.imageUrl,
                    ordinal: Math.random(),
                    imageType: originalAnswer.imageWidth >= originalAnswer.imageHeight ? "w" : "h",
                  })  
                }
              }
              dragDrop.answerPiles = []
              
              return (
                <DragDropScreenView
                  key={dragDropScreen.id}
                  onDragStart={ignore}
                  onDrop={ignore}
                  isTeacher={false}
                  showCorrect={true}
                  onDragStartFromZone={ignore}
                  dragDrop={dragDrop}
                  onDragEnd={ignore}
                  onDragOver={ignore}
                  finished={true}
                  onDragZoneExit={ignore}
                />
              )
            }

            case ScreenType.Drawing: {
              const draw = screen as DrawScreen
              const answers = (screenStats.answers as ReportedDrawingAnswer[])
              let drawingSeries: DrawingSeries[] = []
              for (let a of answers) {
                drawingSeries = drawingSeries.concat(a.drawingCommands.map(x => ({
                  color: x.color,
                  radius: DEFAULT_DRAW_RADIUS,
                  points: x.points
                })))
              }
              return (
                <DrawScreenView
                  key={draw.id}
                  enabled={false}
                  questionText={draw.question}
                  questionImage={draw.questionImageUrl || ""}
                  drawingSeries={drawingSeries}
                  onAddPoint={ignore}
                  onUndo={ignore}
                  onAddSeries={ignore}
                />
              )
            }

            case ScreenType.Keyboard: {
              const keyboard = screen as KeyboardScreen
              const reportedAnswers =
                screenStats.answers as ReportedKeyboardAnswer[]
              const isCorrect =
                reportedAnswers.filter((x) => x.isCorrect).length ===
                reportedAnswers.length
              const text = reportedAnswers.map((x) => x.answerText).join(", ")
              const correct = keyboard.answers.map(x => {
                if (x.isExact) {
                  return format(msg.keyboardAnswerExact, x.content)
                } else {
                  return format(msg.keyboardAnswerExact, x.content)
                }
              }).join((", "))

              return (
                <KeyboardScreenView
                  key={keyboard.id}
                  questionText={keyboard.question}
                  questionImage={keyboard.questionImageUrl || undefined}
                  questionImageWidth={keyboard.questionImageWidth}
                  questionImageHeight={keyboard.questionImageHeight}
                  answer={text}
                  isCorrect={isCorrect}
                  isTeacher={false}
                  onSubmitAnswer={ignore}
                  onKeyboardTextChanged={ignore}
                  finished={true}
                  isPreview={true}
                  showCorrectAnswers={true}
                  correctAnswer={correct}
                />
              )
            }

            case ScreenType.Map: {
              const reportedAnswers = screenStats.answers as ReportedMapAnswer[]
              const map = screen as MapScreen
              let pin: MapPin | null = null
              if (reportedAnswers.length > 0) {
                const last = reportedAnswers[reportedAnswers.length - 1]
                pin = {
                  x: last.x,
                  y: last.y,
                }
              }
              const mapAnswers: MapAnswer[] = map.ellipseAnswers.map((x) => ({
                id: x.id!,
                x: x.x,
                y: x.y,
                width: x.width,
                height: x.height,
              }))

              return (
                <MapScreenView
                  key={map.id}
                  normalizedPin={pin}
                  isTeacher={false}
                  questionImage={map.questionImageUrl || ""}
                  questionText={map.question}
                  placeMapPin={ignore}
                  showCorrect={true}
                  mapAnswers={mapAnswers}
                />
              )
            }

            case ScreenType.Quiz: {
              const quiz = screen as QuizScreen
              const reportedAnswers =
                screenStats.answers as ReportedQuizAnswer[]
              const quizAnswers = quiz.answers //.sort((x, y) => x.ordinal - y.ordinal)
              let selectedIndices: number[] = []
              reportedAnswers.forEach((ra) => {
                const indices = ra.answerIndexes
                selectedIndices = selectedIndices.concat(indices)
              })
              // console.log({answers: quiz.answers, reportedAnswers, selectedIndices})
              const answers: PlayQuizAnswer[] = quizAnswers
                .filter((a) => hasText(a.text) || hasText(a.url))
                .map((a) => {
                  const isSelected = selectedIndices.indexOf(a.ordinal) !== -1

                  return {
                    id: a.id!,
                    text: a.text,
                    isCorrect: a.isCorrect,
                    isSelected: isSelected,
                    ordinal: a.ordinal,
                  }
                })

              return (
                <QuizScreenView
                  key={quiz.id}
                  questionText={quiz.question}
                  questionImage={quiz.questionImageUrl || ""}
                  questionImageWidth={quiz.questionImageWidth}
                  questionImageHeight={quiz.questionImageHeight}
                  showCorrect={true}
                  quizAnswers={answers}
                  onAnswerSelected={ignore}
                />
              )
            }

            case ScreenType.Poll: {
              const poll = screen as PollScreen
              const reportedAnswers =
                screenStats.answers as ReportedPollAnswer[]
              const pollAnswers = poll.answers //.sort((x, y) => x.ordinal - y.ordinal)
              let selectedIndices: number[] = []
              reportedAnswers.forEach((ra) => {
                const indices = ra.answerIndexes
                selectedIndices = selectedIndices.concat(indices)
              })
              // console.log({answers: quiz.answers, reportedAnswers, selectedIndices})
              const answers: PlayPollAnswer[] = pollAnswers
                .filter((a) => hasText(a.text) || hasText(a.url))
                .map((a) => {
                  const isSelected = selectedIndices.indexOf(a.ordinal) !== -1

                  return {
                    id: a.id!,
                    text: a.text,
                    isSelected: isSelected,
                    ordinal: a.ordinal,
                    screenId: a.screenId,
                    imageWidth: a.imageWidth,
                    imageHeight: a.imageHeight,
                  }
                })

              return (
                <PollScreenView
                  key={poll.id}
                  questionText={poll.question}
                  questionImage={poll.questionImageUrl || ""}
                  questionImageWidth={poll.questionImageWidth}
                  questionImageHeight={poll.questionImageHeight}
                  pollAnswers={answers}
                  isDisabled={true}
                  onAnswerSelected={ignore}
                />
              )
            }

            case ScreenType.Connect: {
              const connect = screen as ConnectScreen
              const reportedAnswers =
                screenStats.answers as ReportedConnectAnswer[]
              const pairs = convertConnectPairToPlayPair({
                pairs: connect.pairs,
                shuffle: false,
              })
              // console.log(pairs)
              if (reportedAnswers.length > 0) {
                const last = reportedAnswers[reportedAnswers.length - 1]
                if (last.links) {
                  let colorIndex = 0
                  last.links.forEach((link) => {
                    const left = findWithIndex(
                      pairs,
                      (x) => x.left.id === link.leftPairId
                    )
                    const right = findWithIndex(
                      pairs,
                      (x) => x.right.id === link.rightPairId
                    )
                    if (left.item && right.item) {
                      left.item.left.connectedToIdx = right.index
                      right.item.right.connectedToIdx = left.index

                      left.item.left.isSelected = true
                      right.item.right.isSelected = true

                      left.item.left.colorIndex = colorIndex
                      right.item.right.colorIndex = colorIndex
                      colorIndex++
                    }
                  })
                }
              }

              return (
                <ConnectScreenView
                  key={screen.id}
                  isTeacher={true}
                  showCorrect={true}
                  connection={{ left: -1, right: -1 }}
                  questionText={connect.question || ""}
                  onPairSelected={ignore}
                  pairs={pairs}
                />
              )
            }

            case ScreenType.OpenText: {
              const openText = screen as OpenTextScreen
              const reportedAnswers =
                screenStats.answers as ReportedOpenTextAnswer[]

              let answer = ''
              
              if (reportedAnswers.length > 0) {
                const last = reportedAnswers[reportedAnswers.length - 1]
                answer = last.answerText
              }
              
              return (
                <OpenTextScreenView
                  questionText={openText.question}
                  questionImage={openText.questionImageUrl || ""}
                  questionImageWidth={openText.questionImageWidth || 0}
                  questionImageHeight={openText.questionImageHeight || 0}
                  answer={answer}
                  isTeacher={false}
                  finished={true}
                  isPreview={false}
                  onSubmitAnswer={ignore}
                  onAnswerChanged={ignore}
                />
              )
            } 
          }
        })}
    </>
  )
}
